import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';

// Pages
import Landing from './pages/Landing';
import OwnerSignUp from './pages/OwnerSignUp';
import OwnerSignIn from './pages/OwnerSignIn';
import EmployeeLogin from './pages/EmployeeLogin';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Integrations from './pages/Integrations';
import Products from './pages/Products';
import Stock from './pages/Stock';
import Users from './pages/Users';
import Roles from './pages/Roles';
import Settings from './pages/Settings';
import SuperAdmin from './pages/SuperAdmin';
import FastStock from './pages/FastStock';
import ProductDetail from './pages/ProductDetail';
import HR from './pages/HR';
import Sales from './pages/sales';
import Ecommerce from './pages/Ecommerce';
import Research from './pages/Research';
import ResearchProduct from './pages/ResearchProduct';
import Transport from './pages/Transport';
import Logistics from './pages/Logistics';
import ClockTerminal from './pages/ClockTerminal';
import StockTerminal from './pages/StockTerminal';
import DocumentTerminal from './pages/DocumentTerminal';
import KioskPicking from './pages/kiosk/KioskPicking';
import RawMaterials from './pages/RawMaterials';
import Documents from './pages/Documents';
import Accounting from './pages/Accounting';
import CompanyDetailPage from './pages/CompanyDetailPage';
import OcrBatchPage from './pages/accounting/OcrBatchPage';
import ImportReviewPage from './pages/accounting/ImportReviewPage';
import InvoiceDetailPage from './pages/accounting/InvoiceDetailPage';
import ImportHistoryPage from './pages/accounting/ImportHistoryPage';
import DuplicatesPage from './pages/DuplicatesPage';
import AgingPage from './pages/AgingPage';
import Reports from './pages/Reports';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, pageName = null }) => {
    const { isAuthenticated, loading, isAdmin, isPlatformOnlyAdmin, user } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    // Legacy password-only super admin has no company — force into /superadmin
    if (isPlatformOnlyAdmin && !window.location.pathname.includes('superadmin')) {
        return <Navigate to="/superadmin" replace />;
    }

    if (adminOnly && !isAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    // Check role-based page access for non-admin employees
    if (pageName && !isAdmin && user?.type === 'employee') {
        const userPages = user?.permissions?.pages || [];
        const defaultPages = ['dashboard', 'stock', 'products', 'fast-stock', 'sales'];
        // No role or no pages = use defaults
        if (!user?.role_id || userPages.length === 0) {
            if (!defaultPages.includes(pageName)) {
                return <Navigate to="/dashboard" replace />;
            }
        } else if (!userPages.includes(pageName)) {
            return <Navigate to="/dashboard" replace />;
        }
    }

    return children;
};

// SuperAdmin Route
const SuperAdminRoute = ({ children }) => {
    const { isAuthenticated, loading, isSuperAdmin } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!isAuthenticated || !isSuperAdmin) {
        return <Navigate to="/" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/signup" element={<OwnerSignUp />} />
            <Route path="/signin" element={<OwnerSignIn />} />
            <Route path="/employee-login" element={<EmployeeLogin />} />
            <Route path="/auth-callback" element={<AuthCallback />} />
            <Route path="/clock/:companySlug" element={<ClockTerminal />} />
            <Route path="/terminal/stock/:companySlug" element={<StockTerminal />} />
            <Route path="/terminal/documents/:companySlug" element={<DocumentTerminal />} />
            <Route path="/kiosk/picking" element={<KioskPicking />} />
            {/* Research — Public sourcer link (PIN korumalı) */}
            <Route path="/r/:slug" element={<Research shareMode />} />
            <Route path="/r/:slug/p/:pid" element={<ResearchProduct shareMode />} />

            {/* Protected Routes */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/products"
                element={
                    <ProtectedRoute pageName="products">
                        <Products />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/products/new"
                element={
                    <ProtectedRoute pageName="products">
                        <ProductDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/products/:id"
                element={
                    <ProtectedRoute pageName="products">
                        <ProductDetail />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/stock"
                element={
                    <ProtectedRoute pageName="stock">
                        <Stock />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/fast-stock"
                element={
                    <ProtectedRoute pageName="fast-stock">
                        <FastStock />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/sales"
                element={
                    <ProtectedRoute pageName="sales">
                        <Sales />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ecommerce"
                element={
                    <ProtectedRoute pageName="sales">
                        <Ecommerce />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/research"
                element={
                    <ProtectedRoute pageName="sales">
                        <Research />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/research/p/:pid"
                element={
                    <ProtectedRoute pageName="sales">
                        <ResearchProduct />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/transport"
                element={
                    <ProtectedRoute pageName="sales">
                        <Transport />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/logistics"
                element={
                    <ProtectedRoute pageName="sales">
                        <Logistics />
                    </ProtectedRoute>
                }
            />

            {/* Role-based Routes */}
            <Route
                path="/hr"
                element={
                    <ProtectedRoute pageName="hr">
                        <HR />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/attendance"
                element={
                    <ProtectedRoute pageName="hr">
                        <HR />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/attendance/log"
                element={
                    <ProtectedRoute pageName="hr">
                        <HR />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/workers"
                element={
                    <ProtectedRoute pageName="hr">
                        <HR />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/users"
                element={
                    <ProtectedRoute pageName="users">
                        <Users />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/roles"
                element={
                    <ProtectedRoute pageName="roles">
                        <Roles />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings"
                element={
                    <ProtectedRoute pageName="settings">
                        <Settings />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/integrations"
                element={
                    <ProtectedRoute pageName="integrations">
                        <Integrations />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/raw-materials"
                element={
                    <ProtectedRoute pageName="raw-materials">
                        <RawMaterials />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/documents"
                element={
                    <ProtectedRoute pageName="documents">
                        <Documents />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounting"
                element={
                    <ProtectedRoute pageName="accounting">
                        <Accounting />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounting/companies/:companyId"
                element={
                    <ProtectedRoute pageName="accounting">
                        <CompanyDetailPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounting/duplicates"
                element={
                    <ProtectedRoute pageName="accounting">
                        <DuplicatesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounting/aging"
                element={
                    <ProtectedRoute pageName="accounting">
                        <AgingPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounting/ocr-batch"
                element={
                    <ProtectedRoute pageName="accounting">
                        <OcrBatchPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounting/imports/:batchId"
                element={
                    <ProtectedRoute pageName="accounting">
                        <ImportReviewPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounting/imports-history"
                element={
                    <ProtectedRoute pageName="accounting">
                        <ImportHistoryPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/accounting/invoices/:id"
                element={
                    <ProtectedRoute pageName="accounting">
                        <InvoiceDetailPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/reports"
                element={
                    <ProtectedRoute pageName="reports">
                        <Reports />
                    </ProtectedRoute>
                }
            />

            {/* SuperAdmin Route */}
            <Route
                path="/superadmin"
                element={
                    <SuperAdminRoute>
                        <SuperAdmin />
                    </SuperAdminRoute>
                }
            />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    // Iter 104: React Query — server state cache. 30s stale, 5min cache.
    const queryClient = React.useMemo(() => new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 30_000,
                gcTime: 5 * 60_000,
                refetchOnWindowFocus: false,
                retry: 1,
            },
        },
    }), []);
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <AuthProvider>
                    <SidebarProvider>
                        <AppRoutes />
                    </SidebarProvider>
                    <Toaster
                        position="top-right"
                        offset="70px"
                        toastOptions={{
                            style: {
                                background: '#FFFFFF',
                                border: '1px solid #E5E5E5',
                                borderRadius: '12px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                color: '#1D1D1F',
                                fontFamily: 'Inter, sans-serif'
                            },
                        }}
                    />
                </AuthProvider>
            </BrowserRouter>
        </QueryClientProvider>
    );
}

export default App; 